import { ref, Ref } from "vue";
import ApiService from "@/core/services/ApiService";
import store from "@/store";
import { Actions } from "@/store/enums/StoreEnums";
import { Endpoints } from "@/api/ApiEndpoints";

export interface DashboardEnvelope<T> {
  data: T;
  generatedAt: string;
  stale: boolean;
}

/**
 * Μία ενότητα του dashboard = ένα endpoint που φορτώνει μόνο του.
 * Κρατάει το δικό του loading/error ώστε μια αποτυχία να μη ρίχνει τη σελίδα.
 */
export function useDashboardSection<T>(path: string, initial: T) {
  // Cast to Ref<T> instead of letting `ref<T>()` infer Ref<UnwrapRef<T>> — for a
  // generic T, TS cannot prove T is assignable to its own UnwrapRef<T>, which made
  // `data.value = envelope.data` fail to compile (TS2322) for every call site.
  const data = ref(initial) as Ref<T>;
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);
  const stale = ref(false);
  const generatedAt = ref<string | null>(null);

  async function load(from: string, to: string) {
    loading.value = true;
    error.value = null;
    try {
      // NOTE: ApiService.get(resource, slug, params, key) forwards `params` verbatim
      // to axios as { params }. Passing { params: {...} } here (as the brief drafted
      // it) would produce ?params[from]=...&params[to]=... — pass the query object
      // directly, matching the existing call in AdminDashboard.vue:279.
      const response = await ApiService.get(
        `${Endpoints.ADMIN_DASHBOARD_V2}/${path}`,
        "",
        { from, to }
      );
      const envelope = response.data as DashboardEnvelope<T>;
      data.value = envelope.data;
      stale.value = envelope.stale;
      generatedAt.value = envelope.generatedAt;
      loaded.value = true;
    } catch (e: any) {
      error.value = e?.message ?? "Failed to load";
      // ApiService.get dispatches ADD_BODY_CLASSNAME("page-loading") before the
      // request but only dispatches REMOVE_BODY_CLASSNAME after the await succeeds —
      // there is no try/finally there, and it is a shared file we must not edit.
      // page-loading renders a full-screen overlay; with 8 sections firing in
      // parallel, one failure would otherwise lock the whole admin UI behind it.
      // The global axios error interceptor (ApiService.handleErrors) also removes
      // it for most HTTP error responses, but this call is defensive for any path
      // that throws before reaching that interceptor (e.g. a request-setup error).
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
    } finally {
      loading.value = false;
    }
  }

  return { data, loading, loaded, error, stale, generatedAt, load };
}
